import React, { useEffect, useState } from "react";
import axios from "axios";
axios.defaults.withCredentials = true;

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// -------------------- UTIL --------------------
function getAuthHeaders() {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// -------------------- LOGIN SCREEN --------------------
function AdminLogin({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/admin/login`, {
        email,
        password,
      });

      localStorage.setItem("adminToken", res.data.token);
      onLoggedIn();
      window.location.replace("/admin");
    } catch (err) {
      console.error("Admin login error:", err);
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="w-full max-w-sm bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-2 mb-4 justify-center">
          <div className="w-8 h-8 rounded-full bg-rose-500 flex items-center justify-center text-white font-semibold">
            R
          </div>
          <div className="text-slate-100 font-semibold tracking-tight">
            RelaxB Admin
          </div>
        </div>

        <h1 className="text-lg font-semibold text-slate-50 mb-1 text-center">
          Sign in
        </h1>
        <p className="text-xs text-slate-400 mb-4 text-center">
          Admin access only. Use your dashboard credentials.
        </p>

        {error && (
          <div className="mb-3 text-xs text-rose-400 bg-rose-950/40 border border-rose-700/60 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-3">
          <div>
            <label className="block text-[11px] text-slate-300 mb-1">
              Email
            </label>
            <input
              type="email"
              className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500/70 focus:border-rose-500/70"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-[11px] text-slate-300 mb-1">
              Password
            </label>
            <input
              type="password"
              className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500/70 focus:border-rose-500/70"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full mt-1 py-2.5 rounded-lg text-sm font-medium text-white shadow-sm transition 
              ${
                loading
                  ? "bg-slate-700 cursor-not-allowed"
                  : "bg-rose-500 hover:bg-rose-600"
              }`}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="mt-4 text-[11px] text-slate-500 text-center">
          This area is for internal use. If you’ve lost access, contact the
          site owner.
        </p>
      </div>
    </div>
  );
}

// -------------------- VARIANT ROW --------------------
function VariantRow({ variant, index, onChange, onRemove, onImageChange }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-slate-50">
      <div className="flex gap-2 mb-2">
        <input
          placeholder="Variant name (e.g. Black Leather)"
          className="border border-slate-200 bg-white p-2 rounded w-full text-xs focus:outline-none focus:ring-1 focus:ring-slate-400"
          value={variant.name}
          onChange={(e) => onChange(index, "name", e.target.value)}
        />
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-[10px] bg-rose-500 text-white px-2 rounded hover:bg-rose-600"
        >
          ✕
        </button>
      </div>

      <div className="flex gap-2 mb-2">
        <input
          type="number"
          placeholder="Price override"
          className="border border-slate-200 bg-white p-2 rounded w-1/2 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400"
          value={variant.price}
          onChange={(e) => onChange(index, "price", e.target.value)}
        />
        <input
          type="number"
          placeholder="Stock"
          className="border border-slate-200 bg-white p-2 rounded w-1/2 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400"
          value={variant.stock}
          onChange={(e) => onChange(index, "stock", e.target.value)}
        />
      </div>

      <div>
        <label className="block text-[11px] font-medium mb-1 text-slate-600">
          Variant image (optional)
        </label>
        <input
          type="file"
          accept="image/*"
          className="text-[11px]"
          onChange={(e) => onImageChange(index, e.target.files?.[0] || null)}
        />
      </div>
    </div>
  );
}

// -------------------- CREATE PRODUCT PANEL --------------------
function ProductCreate({ onCreated }) {
  const [form, setForm] = useState({
    name: "",
    price: "",
    category: "",
    description: "",
    stock: 10,
    imageFiles: [],
    variants: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setForm((prev) => ({ ...prev, imageFiles: files }));
  };

  const handleVariantChange = (index, field, value) => {
    setForm((prev) => {
      const variants = [...(prev.variants || [])];
      variants[index] = {
        ...variants[index],
        [field]:
          field === "price" || field === "stock" ? Number(value) : value,
      };
      return { ...prev, variants };
    });
  };

  const handleVariantImageChange = (index, file) => {
    setForm((prev) => {
      const variants = [...(prev.variants || [])];
      variants[index] = { ...variants[index], imageFile: file };
      return { ...prev, variants };
    });
  };

  const handleAddVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...(prev.variants || []),
        { name: "", price: 0, stock: 0, imageFile: null },
      ],
    }));
  };

  const handleRemoveVariant = (index) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const handleCreateProduct = async () => {
    try {
      setError("");

      if (!form.name || !form.price || !form.category) {
        setError("Name, price and category are required.");
        return;
      }

      setSaving(true);

      // 1) Upload main product images
      let productImageUrls = [];
      if (form.imageFiles?.length) {
        for (const file of form.imageFiles) {
          const fd = new FormData();
          fd.append("image", file);

          const uploadRes = await axios.post(
            `${API_BASE}/api/upload`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          productImageUrls.push(uploadRes.data.url);
        }
      }

      // 2) Build variants payload & upload variant images
      const variantsPayload = [];
      for (const v of form.variants || []) {
        if (!v.name) continue;

        let variantImageUrl = "";

        if (v.imageFile) {
          const fd = new FormData();
          fd.append("image", v.imageFile);
          const uploadRes = await axios.post(
            `${API_BASE}/api/upload`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          variantImageUrl = uploadRes.data.url;
        } else if (productImageUrls[0]) {
          variantImageUrl = productImageUrls[0];
        }

        variantsPayload.push({
          name: v.name,
          price: v.price ? Number(v.price) : Number(form.price),
          stock: v.stock ? Number(v.stock) : 0,
          image: variantImageUrl,
        });
      }

      // 3) Create product
      await axios.post(
        `${API_BASE}/api/products`,
        {
          name: form.name,
          category: form.category,
          description: form.description,
          price: Number(form.price),
          images: productImageUrls,
          stock: Number(form.stock),
          variants: variantsPayload,
        },
        { headers: getAuthHeaders() }
      );

      setForm({
        name: "",
        price: "",
        category: "",
        description: "",
        stock: 10,
        imageFiles: [],
        variants: [],
      });

      onCreated?.();
    } catch (err) {
      console.error("Create product error:", err);
      setError("Failed to create product. Check admin login and server.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Create product
          </h2>
          <p className="text-[11px] text-slate-500">
            Add new RelaxB items with optional variants.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <input
          className="border border-slate-200 rounded-lg w-full p-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          placeholder="Product name"
          value={form.name}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, name: e.target.value }))
          }
        />
        <input
          className="border border-slate-200 rounded-lg w-full p-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          placeholder="Category (e.g. Massage Chair)"
          value={form.category}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, category: e.target.value }))
          }
        />
        <div className="flex gap-3">
          <input
            type="number"
            className="border border-slate-200 rounded-lg w-1/2 p-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
            placeholder="Base price (e.g. 1299)"
            value={form.price}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, price: e.target.value }))
            }
          />
          <input
            type="number"
            className="border border-slate-200 rounded-lg w-1/2 p-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
            placeholder="Base stock"
            value={form.stock}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                stock: Number(e.target.value),
              }))
            }
          />
        </div>

        <textarea
          className="border border-slate-200 rounded-lg w-full p-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          rows={3}
          placeholder="Short description"
          value={form.description}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, description: e.target.value }))
          }
        />

        <div>
          <label className="block text-[11px] font-medium text-slate-600 mb-1">
            Product images
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="block w-full text-xs text-slate-600"
          />
          {form.imageFiles?.length > 0 && (
            <p className="text-[11px] text-slate-500 mt-1">
              {form.imageFiles.length} file(s) selected
            </p>
          )}
        </div>

        {/* VARIANTS */}
        <div className="mt-4 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-semibold text-slate-800">
              Product variants (optional)
            </h3>
            <button
              type="button"
              onClick={handleAddVariant}
              className="text-[11px] px-2 py-1 rounded-full bg-slate-900 text-slate-50 hover:bg-slate-800"
            >
              + Add variant
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mb-2">
            Example: “Black Leather”, “With Footrest”, or custom configurations.
          </p>

          {form.variants?.length === 0 && (
            <p className="text-[11px] text-slate-400">
              No variants yet. Click &ldquo;Add variant&rdquo; to create one.
            </p>
          )}

          {form.variants?.map((v, idx) => (
            <VariantRow
              key={idx}
              variant={v}
              index={idx}
              onChange={handleVariantChange}
              onRemove={handleRemoveVariant}
              onImageChange={handleVariantImageChange}
            />
          ))}
        </div>

        <button
          type="button"
          disabled={saving}
          onClick={handleCreateProduct}
          className={`w-full mt-2 py-2.5 rounded-lg text-sm font-medium text-white shadow-sm transition 
            ${
              saving
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-slate-900 hover:bg-slate-800"
            }`}
        >
          {saving ? "Creating product..." : "Create product"}
        </button>
      </div>
    </div>
  );
}

// -------------------- PRODUCT LIST PANEL --------------------
function ProductList({ products, loading, onRefresh, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Products</h2>
          <p className="text-[11px] text-slate-500">
            Manage your RelaxB catalogue.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="text-[11px] px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading products...</p>
      ) : products.length === 0 ? (
        <p className="text-sm text-slate-500">
          No products yet. Create one on the left.
        </p>
      ) : (
        <div className="max-h-[430px] overflow-y-auto border border-slate-200 rounded-lg">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-2 text-left font-medium">Product</th>
                <th className="p-2 text-left font-medium">Price</th>
                <th className="p-2 text-left font-medium">Stock</th>
                <th className="p-2 text-left font-medium">Variants</th>
                <th className="p-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr
                  key={p._id}
                  className="border-t border-slate-100 hover:bg-slate-50/80"
                >
                  <td className="p-2 align-top">
                    <div className="flex items-center gap-2">
                      {p.images?.[0] && (
                        <img
                          src={p.images[0]}
                          alt={p.name}
                          className="w-9 h-9 rounded-md object-cover border border-slate-200"
                        />
                      )}
                      <div>
                        <div className="text-[13px] font-medium text-slate-900">
                          {p.name}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {p.category}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-2 align-top text-[13px] text-slate-800">
                    ${p.price}
                  </td>
                  <td className="p-2 align-top text-[11px]">
                    {p.stock <= 0 ? (
                      <span className="text-rose-600 font-medium">
                        Out of stock
                      </span>
                    ) : p.stock <= 5 ? (
                      <span className="text-amber-600 font-medium">
                        Low ({p.stock})
                      </span>
                    ) : (
                      <span className="text-emerald-600 font-medium">
                        {p.stock} in stock
                      </span>
                    )}
                  </td>
                  <td className="p-2 align-top text-[11px] text-slate-600">
                    {p.variants?.length ? (
                      <ul className="list-disc ml-4 space-y-[2px]">
                        {p.variants.map((v, i) => (
                          <li key={i}>
                            {v.name} — ${v.price} ({v.stock} pcs)
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-slate-400">No variants</span>
                    )}
                  </td>
                  <td className="p-2 align-top whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onEdit(p)}
                      className="text-[11px] bg-slate-900 text-white px-2 py-1 rounded mr-2 hover:bg-slate-800"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(p._id)}
                      className="text-[11px] bg-rose-600 text-white px-2 py-1 rounded hover:bg-rose-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -------------------- EDIT PRODUCT MODAL --------------------
function EditProductModal({
  product,
  open,
  onClose,
  onSaved,
}) {
  const [draft, setDraft] = useState(product);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(product);
    setImageFile(null);
  }, [product]);

  if (!open || !product) return null;

  const handleSave = async () => {
    try {
      setSaving(true);

      let imageUrl = draft.images?.[0] || "";

      if (imageFile) {
        const fd = new FormData();
        fd.append("image", imageFile);
        const uploadRes = await axios.post(
          `${API_BASE}/api/upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        imageUrl = uploadRes.data.url;
      }

      await axios.put(
        `${API_BASE}/api/products/${draft._id}`,
        {
          name: draft.name,
          category: draft.category,
          description: draft.description,
          price: Number(draft.price),
          stock: Number(draft.stock ?? 0),
          images: imageUrl ? [imageUrl] : [],
          variants: draft.variants || [],
        },
        { headers: getAuthHeaders() }
      );

      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Edit product error:", err);
      alert("Failed to update product.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 border border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900">
            Edit product
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-lg"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <input
            className="border border-slate-200 rounded-lg w-full p-2 text-sm"
            placeholder="Name"
            value={draft?.name || ""}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          <input
            className="border border-slate-200 rounded-lg w-full p-2 text-sm"
            placeholder="Category"
            value={draft?.category || ""}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, category: e.target.value }))
            }
          />
          <div className="flex gap-3">
            <input
              type="number"
              className="border border-slate-200 rounded-lg w-1/2 p-2 text-sm"
              placeholder="Price"
              value={draft?.price || ""}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, price: e.target.value }))
              }
            />
            <input
              type="number"
              className="border border-slate-200 rounded-lg w-1/2 p-2 text-sm"
              placeholder="Stock"
              value={draft?.stock ?? 0}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  stock: Number(e.target.value),
                }))
              }
            />
          </div>

          <textarea
            className="border border-slate-200 rounded-lg w-full p-2 text-sm"
            rows={3}
            placeholder="Description"
            value={draft?.description || ""}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, description: e.target.value }))
            }
          />

          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Product image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="text-xs"
            />
            {draft?.images?.[0] && (
              <img
                src={draft.images[0]}
                alt={draft.name}
                className="w-20 h-20 rounded-md object-cover border border-slate-200 mt-2"
              />
            )}
          </div>

          {draft?.variants?.length > 0 && (
            <div className="mt-2 text-[11px] text-slate-600">
              <div className="font-semibold mb-1">Variants:</div>
              <ul className="list-disc ml-4 space-y-[2px]">
                {draft.variants.map((v, i) => (
                  <li key={i}>
                    {v.name} — ${v.price} ({v.stock} pcs)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className={`px-4 py-2 text-xs rounded-lg text-white font-medium ${
              saving ? "bg-slate-400" : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------- ORDERS PANEL --------------------
function OrdersPanel({ orders, loading, onRefresh }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Orders</h2>
          <p className="text-[11px] text-slate-500">
            Stripe-paid orders appear here once webhooks succeed.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="text-[11px] px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading orders...</p>
      ) : orders.length === 0 ? (
        <p className="text-sm text-slate-500">
          No orders yet. When customers pay via Stripe, you’ll see them here.
        </p>
      ) : (
        <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded-lg">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-2 text-left font-medium">Order</th>
                <th className="p-2 text-left font-medium">Customer</th>
                <th className="p-2 text-left font-medium">Amount</th>
                <th className="p-2 text-left font-medium">Status</th>
                <th className="p-2 text-left font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o._id}
                  className="border-t border-slate-100 hover:bg-slate-50/80"
                >
                  <td className="p-2 align-top">
                    <div className="text-[11px] font-mono text-slate-700">
                      #{o._id.slice(-8)}
                    </div>
                  </td>
                  <td className="p-2 align-top text-[11px] text-slate-700">
                    <div>{o.customerName || "—"}</div>
                    <div className="text-slate-400">{o.customerEmail}</div>
                  </td>
                  <td className="p-2 align-top text-[11px] text-slate-800">
                    ${(o.amount / 100).toFixed(2)}
                  </td>
                  <td className="p-2 align-top">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold
                        ${
                          o.status === "paid"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : o.status === "pending"
                            ? "bg-amber-50 text-amber-700 border border-amber-100"
                            : "bg-rose-50 text-rose-700 border border-rose-100"
                        }`}
                    >
                      {o.status}
                    </span>
                  </td>
                  <td className="p-2 align-top text-[11px] text-slate-500">
                    {new Date(o.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -------------------- MAIN ADMIN --------------------
export default function Admin() {
  const [loggedIn, setLoggedIn] = useState(
    !!localStorage.getItem("adminToken")
  );
  const [activeSection, setActiveSection] = useState("products"); // 'products' | 'orders'
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const res = await axios.get(`${API_BASE}/api/orders/admin`, {
  headers: getAuthHeaders(),
});
      setProducts(res.data);
    } catch (err) {
      console.error("Load products error:", err);
      setError("Failed to load products.");
    } finally {
      setLoadingProducts(false);
    }
  };
const loadOrders = async () => {
  setError(""); 
  try {
    setLoadingOrders(true);

    const res = await axios.get(`${API_BASE}/api/orders/admin`, {
      headers: getAuthHeaders(),
      withCredentials: true,
    });

    setOrders(res.data || []);
  } catch (err) {
    console.error("❌ Load orders error:", err.response?.data || err);
    setError("Failed to load orders — check admin authentication.");
  } finally {
    setLoadingOrders(false);
  }
};

  const handleDeleteProduct = async (id) => {
    if (!confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API_BASE}/api/products/${id}`, {
        headers: getAuthHeaders(),
      });
      loadProducts();
    } catch (err) {
      console.error("Delete product error:", err);
      alert("Failed to delete product.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setLoggedIn(false);
    setProducts([]);
    setOrders([]);
    setActiveSection("products");
  };

  useEffect(() => {
    if (loggedIn) {
      loadProducts();
    }
  }, [loggedIn]);

  useEffect(() => {
    if (loggedIn && activeSection === "orders") {
      loadOrders();
    }
  }, [activeSection, loggedIn]);

  if (!loggedIn) {
    return <AdminLogin onLoggedIn={() => setLoggedIn(true)} />;
  }

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-50">
      {/* SIDEBAR */}
      <aside className="w-64 bg-slate-950 border-r border-slate-800 flex flex-col">
        <div className="px-4 pt-4 pb-3 flex items-center gap-2 border-b border-slate-800">
          <div className="w-8 h-8 rounded-xl bg-rose-500 flex items-center justify-center text-white font-semibold text-lg">
            R
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">
              RelaxB Admin
            </div>
            <div className="text-[11px] text-slate-400">
              Store management dashboard
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 text-sm">
          <button
            type="button"
            onClick={() => setActiveSection("products")}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition
              ${
                activeSection === "products"
                  ? "bg-slate-800 text-slate-50"
                  : "text-slate-300 hover:bg-slate-900/60"
              }`}
          >
            <span>📦</span>
            <span>Products</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSection("orders")}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition
              ${
                activeSection === "orders"
                  ? "bg-slate-800 text-slate-50"
                  : "text-slate-300 hover:bg-slate-900/60"
              }`}
          >
            <span>🧾</span>
            <span>Orders</span>
          </button>
        </nav>

        <div className="px-3 pb-4 border-t border-slate-800 pt-3">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-[12px] text-slate-300 hover:bg-slate-900/70"
          >
            <span>Logout</span>
            <span>↩</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 bg-slate-50 text-slate-900">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-sm font-semibold text-slate-900">
              {activeSection === "products" ? "Products" : "Orders"}
            </h1>
            <p className="text-[11px] text-slate-500">
              {activeSection === "products"
                ? "Manage your RelaxB catalogue, prices and stock."
                : "Review paid orders coming from Stripe checkout."}
            </p>
          </div>
        </header>

        <div className="p-6 space-y-4">
          {error && orders.length === 0 && (
  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
    {error}
  </div>
)}
          {activeSection === "products" && (
            <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-5">
              <ProductCreate onCreated={loadProducts} />
              <ProductList
                products={products}
                loading={loadingProducts}
                onRefresh={loadProducts}
                onEdit={(p) => {
                  setEditProduct(p);
                  setEditOpen(true);
                }}
                onDelete={handleDeleteProduct}
              />
            </div>
          )}

          {activeSection === "orders" && (
            <OrdersPanel
              orders={orders}
              loading={loadingOrders}
              onRefresh={loadOrders}
            />
          )}
        </div>
      </main>

      <EditProductModal
        product={editProduct}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={loadProducts}
      />
    </div>
  );
}
